import Input from "components/Input";
import Select from "react-select";
import React, { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import apiService from "services/apiService";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { getEpocinMilliseconds } from "util/epochTime";
import HeaderButtons from "components/Button/HeaderButtons";
// import { MessageSquarePlus, User2, CalendarDays, UserCheck } from "lucide-react";
import { validateNoTrailingOrLeadingWhitespace } from "util/validation";

const schema = yup.object().shape({
  employeeEmail: yup.string().required("Employee Name is required"),
  review: validateNoTrailingOrLeadingWhitespace("Review")
    .max(50, "Maximum 50 characters allowed")
    .required("Review is required"),
});

interface OptionProps {
  value: string;
  label: string;
}

interface EditProps {
  onClose: () => void;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editData: any;
  isViewMode?: boolean;
}

const EditReview = ({ onClose, onSuccess, onError, editData, isViewMode = false, }: EditProps) => {
 const [userdata, setUserData] = React.useState<OptionProps[]>(() => {
    // Only pre-fill if we actually have the name already
    if (editData && editData.employeeName) {
      return [
        {
          value: editData.employeeEmail,
          label: editData.employeeName, 
        },
      ];
    }
    return []; // Leave empty so the Select shows the placeholder instead of the email
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userPage, setUserPage] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [, setIsResolvingName] = useState(!editData?.employeeName);
  const USER_PAGE_SIZE = 10;

  const epochTimestamp = editData.reviewDate;

  // Convert epoch timestamp with fallback
  let dateStores: number;
  try {
    dateStores = getEpocinMilliseconds(epochTimestamp);
    if (isNaN(dateStores) && typeof epochTimestamp === "string") {
      const [day, month, year] = epochTimestamp.split("-").map(Number);
      if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
        dateStores = new Date(year, month - 1, day).getTime();
      } else {
        dateStores = Date.now();
      }
    }
  } catch {
    dateStores = Date.now();
  }

  const methods = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: { ...editData },
  });

  const { formState: { isDirty } } = methods;

const fetchUsers = async (page: number, searchValue: string, append: boolean) => {
    if (loadingUsers) return;
    setLoadingUsers(true);
    
    try {
      // Safely build the query parameters
      const requestParams: Record<string, string | number> = {
        page: page,
        size: USER_PAGE_SIZE,
      };
      if (searchValue.trim()) requestParams.search = searchValue.trim();

      const res = await apiService.get("api/users", { params: requestParams });
      const payload = res.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const users = Array.isArray(payload?.content) ? payload.content : [];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const fetchedOptions = users.map((item: any) => ({
        value: item.email,
        label: item.displayName || item.email,
      }));

      setUserData((prev) => {
        // Put fetched items first so their displayName overwrites the initial email fallback
        const merged = append ? [...prev, ...fetchedOptions] : [...fetchedOptions, ...prev];
        const dedup = new Map<string, OptionProps>();
        
        merged.forEach((item) => {
          if (!dedup.has(item.value)) dedup.set(item.value, item);
        });
        
        return Array.from(dedup.values());
      });

      setUserPage(page);
      if (typeof payload?.last === "boolean") {
        setHasMoreUsers(!payload.last);
      } else if (typeof payload?.totalPages === "number") {
        setHasMoreUsers(page < payload.totalPages - 1);
      } else {
        setHasMoreUsers(fetchedOptions.length === USER_PAGE_SIZE);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Trigger fetch on mount and when search changes
  useEffect(() => {
    const timer = window.setTimeout(() => {
      void fetchUsers(0, userSearch, false);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [userSearch]);

  useEffect(() => {
    const emailToFetch = editData?.employeeEmail;
    
    // Skip if no email, or if we already have the name
    if (!emailToFetch || editData?.employeeName) {
      setIsResolvingName(false);
      return;
    }

    apiService
      .get("api/users", { params: { search: emailToFetch } })
      .then((res) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const users = Array.isArray(res.data?.content) ? res.data.content : [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const exactMatch = users.find((u: any) => u.email === emailToFetch);
        
        if (exactMatch && exactMatch.displayName) {
          setUserData((prev) => {
            const updated = [...prev];
            const index = updated.findIndex((u) => u.value === emailToFetch);
            
            if (index > -1) {
              updated[index] = { ...updated[index], label: exactMatch.displayName };
            } else {
              updated.push({ value: emailToFetch, label: exactMatch.displayName });
            }
            return updated;
          });
        }
      })
      .catch((err) => console.error("Failed to resolve employee name:", err))
      .finally(() => {
        setIsResolvingName(false);
        
        // Safety Fallback: If API completely failed to find the user, show the email so it isn't blank
        setUserData((prev) => {
          if (!prev.find(u => u.value === emailToFetch)) {
            return [...prev, { value: emailToFetch, label: emailToFetch }];
          }
          return prev;
        });
      });
  }, [editData?.employeeEmail, editData?.employeeName]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const submit = async (data: any) => {
    setIsSubmitting(true);
    const newData = { ...data, id: editData.id, reviewDate: dateStores };
    try {
      const res = await apiService.put(`api/review/${editData.id}`, newData);
      if (res.status === 200) {
        onSuccess?.("Review updated successfully");
      } else {
        onError?.("Failed to update review");
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error && "response" in error
          ? ((error as { response?: { data?: string } }).response?.data ?? "Something went wrong")
          : "Something went wrong";
      onError?.(message);
    } finally {
      setIsSubmitting(false);
      onClose();
    }
  };

  // Shared react-select class names (unstyled + classNames pattern)
  const selectClassNames = {
    control: () =>
      `!rounded-lg !min-h-[42px] !border !border-gray-300 dark:!border-gray-600 !bg-gray-100 dark:!bg-gray-700 !text-sm !shadow-none ${isViewMode ? "!cursor-not-allowed !opacity-80" : ""}`,
    valueContainer: () => "!px-3 !py-0.5",
    placeholder: () => "!text-gray-400 dark:!text-gray-500 !text-sm",
    singleValue: () => "!text-gray-900 dark:!text-white !text-sm",
    input: () => "!text-gray-900 dark:!text-white !text-sm",
    dropdownIndicator: () =>
  isViewMode ? "!hidden" : "!px-2 !text-gray-400 dark:!text-gray-500",
    indicatorSeparator: () =>
  isViewMode ? "!hidden" : "!bg-gray-200 dark:!bg-gray-600",
    menu: () =>
      "!bg-white dark:!bg-gray-800 !border !border-gray-200 dark:!border-gray-600 !rounded-lg !shadow-lg !mt-1",
    menuList: () => "!py-1",
    option: ({ isFocused, isSelected }: { isFocused: boolean; isSelected: boolean }) =>
      `!text-sm !px-3 !py-2 ${isSelected
        ? "!bg-blue-600 !text-white"
        : isFocused
          ? "!bg-blue-50 dark:!bg-gray-700 !text-blue-700 dark:!text-blue-300"
          : "!text-gray-800 dark:!text-gray-200"
      }`,
  };

  
  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(submit)} className="space-y-4 pt-1">

        {/* Employee (read-only) */}
        <div>
          <label className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-white mb-1.5">
            {/* <User2 size={14} className="text-blue-500" /> */}
            Employee Name
          </label>
          <Controller
            name="employeeEmail"
            control={methods.control}
            render={({ field }) => (
             <Select
                {...field}
                options={userdata}
                // Remove isDisabled if you want them to be able to change the employee, 
                // otherwise keep it if EditReview implies you can only edit the review text!
                isDisabled={isViewMode}
                isLoading={loadingUsers}
                placeholder="Select Employee"
                classNames={selectClassNames}
                unstyled
                value={userdata.find((o) => o.value === field.value) ?? null}
                onChange={(opt) => field.onChange(opt?.value)}
                
                // --- ADD THESE FOR PAGINATION & SEARCH ---
                onMenuScrollToBottom={() => {
                  if (hasMoreUsers && !loadingUsers) {
                    void fetchUsers(userPage + 1, userSearch, true);
                  }
                }}
                onInputChange={(value, actionMeta) => {
                  if (actionMeta.action === "input-change") setUserSearch(value);
                  if (actionMeta.action === "menu-close") setUserSearch("");
                  return value;
                }}
              />
            )}
          />
        </div>

        {/* Review */}
        <div>
          <label className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-white mb-1.5">
            {/* <MessageSquarePlus size={14} className="text-blue-500" /> */}
            Review <span className="text-red-500">*</span>
          </label>
          <Input
            name="review"
            label=""
            placeholder="Write the review (max 50 characters)"
            compact
             disabled={isViewMode}
          />
        </div>

        {/* Reviewer + Date — two columns */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-white mb-1.5">
              {/* <UserCheck size={14} className="text-blue-500" /> */}
              Reviewer
            </label>
            <Input name="reviewer" label="" placeholder="Reviewer" disabled compact />
          </div>
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-white mb-1.5">
              {/* <CalendarDays size={14} className="text-blue-500" /> */}
              Review Date
            </label>
            {/* Display only — epoch shown as formatted string */}
            <input
              type="text"
              readOnly
              disabled
              value={epochTimestamp}
              className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white text-sm py-2.5 px-3 cursor-not-allowed opacity-80"
            />
            <input type="hidden" name="reviewDate" value={epochTimestamp} />
          </div>
        </div>

        {/* Actions */}
        {!isViewMode && (
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
          <HeaderButtons
            type="button"
            variant="tertiary"
            size="medium"
            onClick={onClose}
          >
            Cancel
          </HeaderButtons>
          <HeaderButtons
            type="submit"
            variant="primary"
            size="medium"
            loading={isSubmitting}
            disabled={!isDirty || isSubmitting}
          >
            {isSubmitting ? "Saving…" : "Save Changes"}
          </HeaderButtons>
        </div>
        )}
      </form>
    </FormProvider>
  );
};

export default EditReview;
